import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { XMarkIcon } from '@heroicons/react/20/solid';
import { bookingApi } from '../../utils/bookingApi';
import { PageHeader, InlineError } from '../../components/ui';
import { formatDayDate } from '../../utils/dates';
import { sessionWhen } from './bookingDates';
import CalendarNav from './CalendarNav';
import SessionDetail from './SessionDetail';
import './booking-shared.css';
import './BookingCalendar.css';
import SessionTypePill from '../../components/sessions/SessionTypePill';
import ApprovalWaitNotice from '../../components/sessions/ApprovalWaitNotice';

export default function BookingCalendar() {
  const [sessions, setSessions] = useState([]);
  const [closures, setClosures] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [selectedSessionId, setSelectedSessionId] = useState(null);
  const [cart, setCart] = useState(() => {
    try {
      const saved = sessionStorage.getItem('booking-cart');
      return saved ? new Map(JSON.parse(saved)) : new Map();
    } catch { return new Map(); }
  });
  const navigate = useNavigate();
  // The day last asked for, so Try again can ask again
  const shownDate = useRef(null);

  useEffect(() => {
    sessionStorage.setItem('booking-cart', JSON.stringify([...cart]));
    window.dispatchEvent(new CustomEvent('booking-cart-update'));
  }, [cart]);

  const getClosureForDate = (date) => {
    const d = new Date(date.getFullYear(), date.getMonth(), date.getDate());
    return closures.find(c => {
      const start = new Date(c.startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(c.endDate);
      end.setHours(23, 59, 59, 999);
      return d >= start && d <= end;
    }) || null;
  };

  const handleNavigate = (date) => {
    shownDate.current = date;
    setSelectedSessionId(null);
    setLoadError(false);
    const y = date.getFullYear();
    const m = date.getMonth() + 1;
    setLoading(true);
    // Fetch both months if week spans a boundary
    const ws = new Date(date);
    ws.setDate(ws.getDate() - ws.getDay());
    ws.setHours(0, 0, 0, 0);
    const we = new Date(ws);
    we.setDate(we.getDate() + 6);
    const fetchMonths = [{ y, m }];
    if (we.getMonth() + 1 !== m || we.getFullYear() !== y) {
      fetchMonths.push({ y: we.getFullYear(), m: we.getMonth() + 1 });
    }
    Promise.all([
      ...fetchMonths.map(({ y: fy, m: fm }) => bookingApi.getSessions(fy, fm)),
      bookingApi.getClosures(),
    ]).then((results) => {
      setSessions(results.slice(0, -1).flatMap(r => r.data));
      setClosures(results[results.length - 1].data);
    }).catch(err => { console.error(err); setLoadError(true); }).finally(() => setLoading(false));
  };

  const hasStarted = (s) => {
    const [h, m] = s.startTime.split(':').map(Number);
    const start = new Date(s.date);
    start.setHours(h, m, 0, 0);
    return new Date() >= start;
  };

  const sessionClass = (s, isPast) => {
    if (s.isBooked) return 'booked';
    if (s.availableSlots > 0 && !s.cancelledAt && !isPast && !hasStarted(s)) return 'open';
    return 'full';
  };

  const sessionLabel = (s, isPast) => {
    if (s.cancelledAt) return 'Cancelled';
    if (s.isBooked) return 'Booked';
    // A full session still opens, to join its waitlist
    if (s.availableSlots === 0) return isPast || hasStarted(s) ? 'Full' : 'Full, join waitlist';
    if (hasStarted(s)) return 'Started';
    return `${s.availableSlots} left`;
  };

  const handleCartUpdate = (sessionId, gymnasts) => {
    setCart(prev => {
      const next = new Map(prev);
      if (gymnasts.length === 0) next.delete(sessionId);
      else next.set(sessionId, gymnasts);
      return next;
    });
  };

  const cartEntries = Array.from(cart.entries());
  const cartTotalSlots = cartEntries.reduce((sum, [, g]) => sum + g.length, 0);
  // Only when every session in the cart is on screen with its price; the
  // cart and the payment page show the full figures
  const cartPrices = cartEntries.map(([sessionId, gymnasts]) => {
    const session = sessions.find(s => s.id === sessionId);
    return typeof session?.pricePerGymnast === 'number' ? session.pricePerGymnast * gymnasts.length : null;
  });
  const cartTotalAmount = cartPrices.includes(null) ? null : cartPrices.reduce((sum, p) => sum + p, 0);

  const handleCartCheckout = () => {
    navigate('/booking/cart');
  };

  return (
    <div className="bk-page bk-page--narrow">
      <PageHeader title="Book a session" />
      {loadError && (
        <InlineError onRetry={() => handleNavigate(shownDate.current || new Date())}>Couldn't load sessions.</InlineError>
      )}
      <ApprovalWaitNotice />
      <CalendarNav
        sessions={sessions}
        onNavigate={handleNavigate}
        loading={loading}
        closures={closures}
        renderDayDots={(date, daySessions, isPast, isClosed) => {
          if (isClosed) return null;
          return daySessions.filter(s => !s.cancelledAt || s.bookedCount > 0).slice(0, 3).map((s, i) => {
            const type = sessionClass(s, isPast);
            return (
              <span
                key={i}
                className={`booking-calendar__week-dot${
                  type === 'booked' ? ' booking-calendar__week-dot--booked'
                  : type === 'full' ? ' booking-calendar__week-dot--full'
                  : ''
                }`}
              />
            );
          });
        }}
        renderDayPanel={(date, daySessions, isPast, isClosed) => (
          <>
            <p className="booking-calendar__day-detail-heading">
              {formatDayDate(date)}
            </p>
            {selectedSessionId ? (
              <SessionDetail
                instanceId={selectedSessionId}
                onClose={() => setSelectedSessionId(null)}
                cartMode
                cartGymnastIds={(cart.get(selectedSessionId) || []).map(g => g.id)}
                onCartUpdate={handleCartUpdate}
              />
            ) : (
              <>
                {isClosed && (
                  <p className="booking-calendar__day-closed">
                    Closed{getClosureForDate(date)?.reason ? `: ${getClosureForDate(date).reason}` : ''}
                  </p>
                )}
                {!isClosed && daySessions.length === 0 && (
                  <p className="booking-calendar__day-empty">No sessions</p>
                )}
                {!isClosed && daySessions.filter(s => !s.cancelledAt || s.bookedCount > 0).map(s => (
                  <button
                    key={s.id}
                    className={`booking-calendar__day-session booking-calendar__day-session--${sessionClass(s, isPast)}`}
                    disabled={!!s.cancelledAt || isPast || hasStarted(s)}
                    onClick={() => setSelectedSessionId(s.id)}
                  >
                    <span className="booking-calendar__day-session-time">{s.startTime} to {s.endTime}
                      <SessionTypePill session={s} />
                      {s.minAge && <span className="bk-pill bk-pill--info">{s.minAge}+</span>}
                    </span>
                    <span
                      className={[
                        'booking-calendar__day-session-status',
                        !s.isBooked && !s.cancelledAt && s.availableSlots > 0 && s.availableSlots <= 3 ? 'booking-calendar__few-left'
                          : !s.isBooked && !s.cancelledAt && s.availableSlots > 3 && s.availableSlots <= 5 ? 'booking-calendar__some-left'
                          : '',
                      ].join(' ').trim()}
                    >
                      {sessionLabel(s, isPast)}
                    </span>
                  </button>
                ))}
              </>
            )}
          </>
        )}
        renderMonthCell={(date, daySessions, isToday, isPast, isClosed) => (
          <>
            {isClosed && (
              <>
                <span className="booking-calendar__closed-label">Closed</span>
                {getClosureForDate(date)?.reason && (
                  <span className="booking-calendar__closed-reason">{getClosureForDate(date).reason}</span>
                )}
              </>
            )}
            {!isClosed && daySessions.filter(s => !s.cancelledAt || s.bookedCount > 0).map(s => (
              <div key={s.id} className={`booking-calendar__session booking-calendar__session--${sessionClass(s, isPast)}`}>
                {s.startTime}
              </div>
            ))}
          </>
        )}
        getMonthCellClass={(date, daySessions, isPast, isClosed) => {
          if (isClosed) return 'booking-calendar__cell--closed';
          if (isPast) return 'booking-calendar__cell--past';
          if (daySessions.some(s => s.isBooked)) return 'booking-calendar__cell--booked';
          const hasAvailable = daySessions.some(s => s.availableSlots > 0 && !s.cancelledAt);
          if (hasAvailable) return 'booking-calendar__cell--available';
          if (daySessions.length > 0) return 'booking-calendar__cell--full';
          return '';
        }}
      />

      {cartTotalSlots > 0 && (
        <div className="booking-calendar__cart-bar">
          <div className="booking-calendar__cart-items">
            {cartEntries.map(([sessionId, gymnasts]) => {
              const session = sessions.find(s => s.id === sessionId);
              return (
                <div key={sessionId} className="booking-calendar__cart-item">
                  <span>
                    {session ? sessionWhen(session.date, session) : sessionId}
                    {': '}{gymnasts.map(g => g.firstName).join(', ')}
                  </span>
                  <button
                    type="button"
                    className="ui-icon-btn"
                    aria-label="Remove"
                    onClick={() => handleCartUpdate(sessionId, [])}
                  >
                    <XMarkIcon aria-hidden="true" />
                  </button>
                </div>
              );
            })}
          </div>
          <div className="booking-calendar__cart-summary">
            <span>{cartTotalSlots} slot{cartTotalSlots !== 1 ? 's' : ''}{cartTotalAmount !== null ? ` · £${(cartTotalAmount / 100).toFixed(2)}` : ''}</span>
            <button type="button" className="bk-btn bk-btn--sm" onClick={handleCartCheckout}>
              Checkout
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
